import { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { resetStars, useStars } from "@/lib/stars";

// v48b — Creator Grid stars live in the account (scope "grid"). The old
// browser-only list (KEY) is moved in once per account per browser and
// left in place, so nothing is ever lost; the import is idempotent.
const KEY = "kolradar:favorites";
const movedKey = (uid) => `kolradar:favorites:moved:${uid}`;
const FavoritesCtx = createContext(null);

async function moveBrowserStars(uid) {
  let ids = [];
  try {
    if (localStorage.getItem(movedKey(uid))) return;
    ids = JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch { return; }
  if (!Array.isArray(ids) || ids.length === 0) {
    try { localStorage.setItem(movedKey(uid), new Date().toISOString()); } catch { /* ignore */ }
    return;
  }
  try {
    await api.post("/stars/import", { scope: "grid", ids });
    localStorage.setItem(movedKey(uid), new Date().toISOString());
  } catch { /* retried on the next visit */ }
}

export function FavoritesProvider({ children }) {
  const { user } = useAuth() || {};
  const uid = user ? (user.user_id || user.email) : null;
  const [movedFor, setMovedFor] = useState(null);
  useEffect(() => {
    resetStars();
    if (uid) moveBrowserStars(uid).finally(() => setMovedFor(uid));
  }, [uid]);
  // Load account stars only after any browser stars were moved in.
  const stars = useStars(uid && movedFor === uid ? "grid" : null);
  return (
    <FavoritesCtx.Provider value={{ ids: stars.ids, has: stars.has, toggle: stars.toggle, count: stars.count, savedVersion: stars.savedVersion }}>
      {children}
    </FavoritesCtx.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesCtx);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
}
